import { OrcamentoDialog } from './OrcamentoUI';
import AppIcon from './AppIcon';
import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTardios } from '../context/TardiosContext';
import { useAlertasVeiculos } from '../context/AlertasVeiculosContext';
import { useAvisoVencimentos } from '../context/AvisoVencimentosContext';
import { useNotificacoes } from '../context/NotificacoesContext';
import { usePrazosSgi } from '../context/PrazosSgiContext';

// Mapeamento de paths para chaves de permissão
const PERMISSION_KEYS = {
    '/': 'dashboard',
    '/obras': 'obras',
    '/trabalhadores': 'trabalhadores',
    '/recursos-humanos': 'recursos-humanos',
    '/equipamentos': 'equipamentos',
    '/veiculos': 'veiculos',
    '/planeamento': 'planeamento',
    '/manutencoes': 'manutencoes',
    '/maquetes': 'maquetes',
    '/orcamentos': 'orcamentos',
    '/gestao-financeira': 'gestao-financeira',
    '/despesas': 'despesas',
    '/relatorios': 'relatorios',
    '/sistema-integrado': 'sistema-integrado',
    '/notificacoes': 'notificacoes',
};
import './Sidebar.css';
import SidebarThemeIcon from './SidebarThemeIcon';
import { useMaquetesTheme } from '../context/MaquetesThemeContext';

const DEFAULT_MENU = [
    { path: '/', icon: '📊', label: 'Dashboard',        iconSimple: '⊞' },
    { path: '/obras', icon: '🏗️', label: 'Obras',       iconSimple: '⌂' },
    { path: '/recursos-humanos', icon: '🧑‍💼', label: 'Recursos Humanos', iconSimple: '☰' },
    { path: '/equipamentos', icon: '🚜', label: 'Equipamentos',  iconSimple: '⊙' },
    { path: '/veiculos', icon: '🚗', label: 'Veículos',          iconSimple: '◈' },
    { path: '/planeamento', icon: '📅', label: 'Planeamento',    iconSimple: '▦' },
    { path: '/manutencoes', icon: '🔧', label: 'Manutenção',     iconSimple: '⚒' },
    { path: '/maquetes', icon: '🎨', label: 'Maquetes',          iconSimple: '◇' },
    { path: '/orcamentos', icon: '💼', label: 'Orçamentos',      iconSimple: '▤' },
    { path: '/gestao-financeira', icon: '🏦', label: 'Gestão Financeira', iconSimple: '◫' },
    { path: '/despesas', icon: '💳', label: 'Cartões',           iconSimple: '◻' },
    { path: '/relatorios', icon: '📊', label: 'Relatórios',      iconSimple: '▣' },
    { path: '/sistema-integrado', icon: '🎯', label: 'Sistema Integrado', iconSimple: '◎' },
    { path: '/notificacoes', icon: '🔔', label: 'Notificações', iconSimple: '◉' },
];

// Cada tema tem uma combinação fixa; as antigas opções independentes deixam de ser lidas.
const THEME_PRESETS = {
    original: { sidebar: 'dark', page: 'default' },
    liquido: { sidebar: 'dark', page: 'default' },
};
const PIN_KEY = 'sidebar_pinned';

const Sidebar = () => {
    const { active: maquetesObba, theme: maquetesTheme, setTheme: setMaquetesTheme } = useMaquetesTheme();
    const { user, logout } = useAuth();
    const { totalMeses: tardiosMeses } = useTardios();
    const { urgentes: notifUrgentes } = useNotificacoes();
    const { total: alertasViaturas } = useAlertasVeiculos();
    const { total: avisoVencimentos, pendentes: vencPendentes } = useAvisoVencimentos();
    const tituloVenc = (vencPendentes || []).map(p => `${p.n} valores José Tripa em ${p.periodo.split('-').reverse().join('/')}`).join('; ');
    const { resumo: prazosSgi } = usePrazosSgi();
    const prazosAtrasados = prazosSgi?.resumo?.atrasado || 0;
    const navigate = useNavigate();
    const userId = user?.id || user?.username || 'guest';

    const preset = THEME_PRESETS[maquetesTheme] || THEME_PRESETS.original;
    const theme = preset.sidebar;
    const pageTheme = preset.page;

    /* ---- pin: true = barra aberta fixa; false = só ícones, expande no hover ---- */
    const [pinned, setPinned] = useState(() => {
        const v = localStorage.getItem(`${PIN_KEY}_${userId}`);
        return v === null ? true : v === 'true';
    });

    /* hover expand (só quando unpinned) */
    const [hovered, setHovered] = useState(false);

    /* settings panel */
    const [settingsOpen, setSettingsOpen] = useState(false);
    const settingsRef = useRef(null);

    /* reorder */
    const orderKey = `sidebar_order_${userId}`;
    const [reordering, setReordering] = useState(false);
    const [items, setItems] = useState(() => {
        try {
            const saved = localStorage.getItem(`sidebar_order_${user?.id || user?.username || 'guest'}`);
            if (saved) {
                const savedPaths = JSON.parse(saved);
                const sorted = savedPaths
                    .map(path => DEFAULT_MENU.find(m => m.path === path))
                    .filter(Boolean);
                DEFAULT_MENU.forEach(m => {
                    if (!sorted.find(s => s.path === m.path)) sorted.push(m);
                });
                return sorted;
            }
        } catch {}
        return DEFAULT_MENU;
    });
    const [dragIndex, setDragIndex] = useState(null);

    /* ---- barra expandida quando: pinned OU (hover enquanto unpinned) ---- */
    const expanded = pinned || hovered;

    /* sync CSS var para o main-content acompanhar */
    useEffect(() => {
        const w = expanded ? (theme === 'dark-slim' ? '200px' : '230px') : '62px';
        document.documentElement.style.setProperty('--sidebar-width', w);
    }, [expanded, theme]);

    /* aplicar tema de página no body */
    useEffect(() => {
        document.body.setAttribute('data-page-theme', maquetesObba ? 'default' : pageTheme);
        return () => document.body.removeAttribute('data-page-theme');
    }, [pageTheme, maquetesObba]);


    /* handlers */
    const handleLogout = () => { 
        if (window.__isOrcamentoDirty) {
            if (!window.confirm("Tem alterações não guardadas no seu Orçamento. Deseja sair sem gravar?")) {
                return;
            }
        }
        logout(); 
        navigate('/login'); 
    };

    const saveOrder = (arr) =>
        localStorage.setItem(orderKey, JSON.stringify(arr.map(i => i.path)));

    const handleDragStart = (e, idx) => { setDragIndex(idx); e.dataTransfer.effectAllowed = 'move'; };
    const handleDragOver  = (e, idx) => {
        e.preventDefault();
        if (dragIndex === null || dragIndex === idx) return;
        const next = [...items];
        const [moved] = next.splice(dragIndex, 1);
        next.splice(idx, 0, moved);
        setDragIndex(idx);
        setItems(next);
    };
    const handleDragEnd   = () => { setDragIndex(null); saveOrder(items); };

    const moveItem = (idx, dir) => {
        const next = [...items];
        const t = idx + dir;
        if (t < 0 || t >= next.length) return;
        [next[idx], next[t]] = [next[t], next[idx]];
        setItems(next);
        saveOrder(next);
    };

    const handlePinToggle = () => {
        const next = !pinned;
        setPinned(next);
        localStorage.setItem(`${PIN_KEY}_${userId}`, String(next));
        if (!next) setHovered(false); // garantir que colapsa ao despinar
    };

    /* admin items */
    let adminItems = [];
    if (user?.role === 'admin') {
        adminItems = [
            { path: '/utilizadores', icon: '👥', label: 'Utilizadores' },
            { path: '/admin-fichas', icon: '⚙️', label: 'Fichas (Admin)' },
            { path: '/configuracoes', icon: '🛠️', label: 'Configurações' },
            { path: '/lixeira', icon: '🗑️', label: 'Lixo' },
        ];
    }

    // As Notificações ficam sempre no fim da barra, depois dos itens de
    // administração. Sai da ordem normal de propósito: é um destino a que se vai
    // quando há algo a tratar, não uma secção de trabalho como as outras.
    const semNotificacoes = items.filter(i => i.path !== '/notificacoes');
    const notificacoes = items.filter(i => i.path === '/notificacoes');
    const allItems = [...semNotificacoes, ...adminItems, ...notificacoes];

    // Filtrar por permissões do utilizador
    const filterByPermissions = (menuItems) => {
        if (!user) return menuItems;
        if (user.role === 'admin') return menuItems; // admin vê tudo
        if (!user.permissoes) return menuItems; // sem restrições = tudo visível
        return menuItems.filter(item => {
            if (item.path === '/recursos-humanos') return user.permissoes['trabalhadores'] !== false || user.permissoes['recursos-humanos'] !== false;
            const key = PERMISSION_KEYS[item.path];
            if (!key) return true; // itens sem chave de permissão são sempre visíveis
            return user.permissoes[key] !== false;
        });
    };

    const displayItems = user?.perfil_mobile
        ? [
            { path: '/mobile-dashboard', icon: '📱', label: 'Menu Mobile' },
            ...allItems.filter(i => i.path === '/despesas'),
          ]
        : filterByPermissions(allItems);

    return (
        <div
            className={`sidebar sidebar-theme-${theme}${expanded ? '' : ' sidebar-collapsed'}`}
            onMouseEnter={() => { if (!pinned) setHovered(true); }}
            onMouseLeave={() => { if (!pinned) { setHovered(false); setSettingsOpen(false); } }}
        >
            {/* HEADER — logo completo ou ícone conforme estado */}
            <div className="sidebar-header">
                {expanded
                    ? <img src={(!maquetesObba && theme === 'light') ? '/assets/SPROD_NOVO_BK.png' : '/assets/logo-white.png'} alt="SPROD" className="sidebar-logo-full" />
                    : <img src={(!maquetesObba && theme === 'light') ? '/assets/p_bk.png' : '/assets/p_w.png'} alt="P" className="sidebar-logo-icon" />
                }
            </div>

            {/* CONTROLS: pin + reorder (só quando expandido) */}
            <div className="sidebar-controls">
                <button
                    className={`sidebar-ctrl-btn sidebar-pin-btn${pinned ? ' pinned' : ''}`}
                    onClick={handlePinToggle}
                    title={pinned ? 'Soltar barra' : 'Fixar barra'}
                >
                    {maquetesObba ? <SidebarThemeIcon name="pin" /> : theme === 'simple'
                        ? (pinned ? '⊣' : '⊢')
                        : (pinned ? '📌' : '📍')
                    }
                </button>
                {expanded && (
                    <button
                        className={`sidebar-ctrl-btn${reordering ? ' active' : ''}`}
                        onClick={() => setReordering(r => !r)}
                        title="Reordenar menu"
                    >
                        {maquetesObba ? <SidebarThemeIcon name="menu" /> : '☰'}
                    </button>
                )}
            </div>

            {reordering && expanded && (
                <div className="sidebar-reorder-hint">Arraste ou use ↑↓ para reordenar</div>
            )}

            {/* NAV */}
            <nav className="sidebar-nav">
                {displayItems.map((item, idx) => (
                    reordering && expanded ? (
                        <div
                            key={item.path}
                            className={`sidebar-link reorder-item${dragIndex === idx ? ' dragging' : ''}`}
                            draggable
                            onDragStart={e => handleDragStart(e, idx)}
                            onDragOver={e => handleDragOver(e, idx)}
                            onDragEnd={handleDragEnd}
                        >
                            <span className="sidebar-drag-handle">⠿</span>
                            <span className="sidebar-icon">{maquetesObba ? <SidebarThemeIcon name={item.path} /> : (theme === 'simple' && item.iconSimple ? item.iconSimple : item.icon)}</span>
                            <span className="sidebar-label">{item.label}</span>
                            <span className="sidebar-reorder-arrows">
                                <button onClick={() => moveItem(idx, -1)} disabled={idx === 0}>▲</button>
                                <button onClick={() => moveItem(idx, 1)} disabled={idx === displayItems.length - 1}>▼</button>
                            </span>
                        </div>
                    ) : (
                        <NavLink
                            key={item.path}
                            to={item.path}
                            aria-label={item.label}
                            className={({ isActive }) => `sidebar-link${isActive ? ' active' : ''}`}
                            title={!expanded ? item.label : undefined}
                            onClick={(e) => {
                                if (window.__isOrcamentoDirty) {
                                    if (!window.confirm("Tem alterações não guardadas no seu Orçamento. Deseja sair sem gravar?")) {
                                        e.preventDefault();
                                    }
                                }
                            }}
                        >
                            <span className="sidebar-icon">{maquetesObba ? <SidebarThemeIcon name={item.path} /> : (theme === 'simple' && item.iconSimple ? item.iconSimple : item.icon)}</span>
                            {expanded && <span className="sidebar-label">{item.label}</span>}
                            {/* Registos tardios: despesas de cartão carregadas depois de o mês
                                da fatura já ter sido fechado. Conta MESES por rever, não
                                despesas — é o mês a unidade de trabalho a refazer.
                                Irmão do ícone e nunca dentro dele: no tema "simple" o
                                .sidebar-icon força monospace e cor fixa, que o número herdaria.
                                Só no ramo NavLink — em modo reordenar as setas ▲▼ já usam
                                margin-left:auto e disputariam o espaço. */}
                            {/* Só para quem trabalha no computador: o painel dos tardios
                                nem sequer é desenhado para os perfis de telemóvel, e num
                                tablet deitado a barra lateral aparece à mesma — dava um
                                contador de fecho de mês a quem não tem como o abrir. */}
                            {item.path === '/despesas' && tardiosMeses > 0 && !user?.perfil_mobile && (
                                expanded
                                    ? <span className="sidebar-badge"
                                        title={`${tardiosMeses} mês(es) com despesas carregadas fora de prazo`}>
                                        {tardiosMeses}
                                      </span>
                                    : <span className="sidebar-badge-dot"
                                        title={`${tardiosMeses} mês(es) com despesas carregadas fora de prazo`} />
                            )}
                            {/* Vencimentos: mais de 5 valores José Tripa introduzidos num mês
                                (valores prontos para os salários). Desliga-se nos Vencimentos. */}
                            {item.path === '/recursos-humanos' && avisoVencimentos > 0 && (
                                expanded
                                    ? <span className="sidebar-badge" title={`Vencimentos: ${tituloVenc}`}>
                                        {avisoVencimentos}
                                      </span>
                                    : <span className="sidebar-badge-dot" title={`Vencimentos: ${tituloVenc}`} />
                            )}
                            {/* Seguro ou inspeção a um mês, revisão a 2500 km. */}
                            {item.path === '/veiculos' && alertasViaturas > 0 && (
                                expanded
                                    ? <span className="sidebar-badge"
                                        title={`${alertasViaturas} registo(s) de viaturas a precisar de atenção`}>
                                        {alertasViaturas}
                                      </span>
                                    : <span className="sidebar-badge-dot"
                                        title={`${alertasViaturas} registo(s) de viaturas a precisar de atenção`} />
                            )}
                            {/* Prazos do Sistema Integrado já ultrapassados. Só os
                                atrasados: os que ainda vão vencer mostram-se dentro do
                                Sistema Integrado, e um número que somasse tudo deixava de
                                querer dizer "isto já passou do prazo". O contexto só pede
                                a quem tem o módulo — sem ele o número fica a zero. */}
                            {item.path === '/sistema-integrado' && prazosAtrasados > 0 && (
                                expanded
                                    ? <span className="sidebar-badge"
                                        title={`${prazosAtrasados} prazo(s) do Sistema Integrado em atraso`}>
                                        {prazosAtrasados}
                                      </span>
                                    : <span className="sidebar-badge-dot"
                                        title={`${prazosAtrasados} prazo(s) do Sistema Integrado em atraso`} />
                            )}
                            {/* Só o que é iminente: contratos cujo prazo para comunicar
                                a não renovação está à vista — os que piscam no ecrã. */}
                            {item.path === '/notificacoes' && notifUrgentes > 0 && (
                                expanded
                                    ? <span className="sidebar-badge"
                                        title={`${notifUrgentes} contrato(s) com o prazo de comunicação a terminar`}>
                                        {notifUrgentes}
                                      </span>
                                    : <span className="sidebar-badge-dot"
                                        title={`${notifUrgentes} contrato(s) com o prazo de comunicação a terminar`} />
                            )}
                        </NavLink>
                    )
                ))}
            </nav>

            {/* FOOTER */}
            <div className="sidebar-footer">
                {/* user info */}
                <div className="sidebar-footer-user">
                    <span className="user-avatar">{maquetesObba ? <SidebarThemeIcon name="user" /> : '👤'}</span>
                    {expanded && (
                        <div className="user-details">
                            <span className="user-name">{user?.nome_completo || user?.username}</span>
                            <span className="user-role">{user?.role}</span>
                        </div>
                    )}
                </div>

                {/* gear + logout */}
                <div className="sidebar-footer-actions">
                    <button
                        className={`sidebar-gear-btn${settingsOpen ? ' active' : ''}`}
                        onClick={() => setSettingsOpen(o => !o)}
                        title="Definições" aria-label="Definições de aparência"
                    >
                        {maquetesObba ? <SidebarThemeIcon name="settings" /> : '⚙'}
                    </button>

                    {expanded && (
                        <button onClick={handleLogout} className="logout-btn">Sair</button>
                    )}
                </div>

                {/* Modal de definições — centrado no ecrã */}
                {settingsOpen && (
                    <OrcamentoDialog data-appearance-dialog="true" label="Definições de aparência" onClose={() => setSettingsOpen(false)}>
                        <div className="settings-modal" ref={settingsRef} onClick={e => e.stopPropagation()}>
                            <div className="settings-modal-header">
                                <h3><AppIcon name="settings" size={18}/> Definições de aparência</h3>
                                <button className="settings-modal-close" aria-label="Fechar" onClick={() => setSettingsOpen(false)}><AppIcon name="close" size={16}/></button>
                            </div>

                            <div className="settings-modal-body">
                                <div className="settings-modal-section-title">Tema</div>
                                <p style={{ fontSize: '12px', color: '#64748b', margin: '0 0 12px' }}>Escolha um tema. A barra lateral, as cores e os fundos são aplicados em conjunto. A escolha fica guardada para si neste navegador.</p>
                                <div className="settings-modal-grid">
                                    {[{ key: 'original', label: 'Classic', desc: 'A aparência habitual da aplicação' }, { key: 'liquido', label: 'Standard', desc: 'Vidro, barra flutuante e acentos azuis' }].map(t => (
                                        <button key={t.key} aria-pressed={maquetesTheme === t.key}
                                            className={`settings-modal-opt${maquetesTheme === t.key ? ' active' : ''}`}
                                            onClick={() => setMaquetesTheme(t.key)}>
                                            <span className="theme-dot" style={{ background: t.key === 'liquido' ? 'linear-gradient(135deg, #162438, #589ee5, #9bbfea)' : t.key === 'claro' ? 'linear-gradient(135deg, #1764aa, #78b9ef)' : '#edf2f7' }} />
                                            <div><div className="theme-opt-label">{t.label}</div><div className="theme-opt-desc">{t.desc}</div></div>
                                            {maquetesTheme === t.key && <span className="theme-check">✓</span>}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </OrcamentoDialog>
                )}

                {!expanded && (
                    <button onClick={handleLogout} className="logout-btn logout-btn-icon" title="Sair">{maquetesObba ? <SidebarThemeIcon name="exit" /> : '⏻'}</button>
                )}
            </div>
        </div>
    );
};

export default Sidebar;
